import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { hidePartnerApiLogDetails } from 'app/features/partner-api-logs/partnerApiLogSlice';

const sx = {
  jsonValue: {
    mt: 0,
    p: 1.25,
    backgroundColor: '#f7f9fc',
    borderRadius: '0 0 6px 6px',
    fontFamily: 'monospace',
    fontSize: 12,
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    border: '1px solid',
    borderColor: 'divider',
    borderTop: 'none',
  },
  logToggleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    px: 1.5,
    py: 0.75,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    '&:hover': { backgroundColor: 'action.hover' },
  },
};

const formatJson = (value) => {
  if (value == null || value === '') return '-';
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return JSON.stringify(parsed, null, 2);
  } catch {
    return value;
  }
};

function JsonToggleField({ label, value, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const toggleOpen = () => {
    setOpen(!open);
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Box component="button" onClick={() => toggleOpen()} sx={[sx.logToggleButton]}>
        <Typography variant="body2" fontWeight={600} color="text.secondary" fontSize={12}>
          {label}
        </Typography>
        {open ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        )}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box component="pre" sx={sx.jsonValue}>
          {formatJson(value)}
        </Box>
      </Collapse>
    </Box>
  );
}

function PartnerApiLogModal() {
  const dispatch = useDispatch();

  const { showBriefDetailModal, isLoading, data, errorMessage } = useSelector(
    (state) => state.partnerApiLogs.show,
  );

  const handleClose = () => dispatch(hidePartnerApiLogDetails());

  return (
    <Dialog
      open={showBriefDetailModal ?? false}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh', maxWidth: '52vw' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          pr: 1,
        }}
      >
        <Typography component="span" variant="h6" fontWeight={600}>
          Partner API Log
        </Typography>
        <IconButton aria-label="close" onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        )}
        {errorMessage && (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {errorMessage}
          </Typography>
        )}
        {!isLoading && (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '140px 1fr',
                gap: 1,
                alignItems: 'center',
                mb: 1.5,
                px: 0.5,
              }}
            >
              <Typography variant="body2" fontWeight={600} color="text.secondary" fontSize={12}>
                Endpoint
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {data?.endpoint || '-'}
              </Typography>
            </Box>
            <JsonToggleField label="Request Body" value={data?.requestBody} defaultOpen />
            <JsonToggleField label="Response Body" value={data?.responseBody} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PartnerApiLogModal;
